import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { PageState, Skeleton, TabBar } from "@fox1ck-org/ui";
import type { Campaign, CampaignListItem } from "@gen/tracker/v1/campaign_pb";

import { CampaignHeader } from "@/components/campaign/card-header";
import { useCrumbTail } from "@/lib/crumb";
import { useIsPhone } from "@/lib/hooks/use-phone";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useRouteTab } from "@/lib/route-tab";
import { useT, type TKey } from "@/lib/i18n";
import { derive, type Bag } from "@/lib/campaigns-table";
import { statsRowToNumbers, useReportingSchema } from "@/lib/services/reporting-schema-service";
import { useCampaignCard, useGetCampaign } from "@/lib/services/campaign-service";

import { SummaryTab } from "./summary-tab";
import { ReportTab } from "./report-tab";
import { ConversionsTab } from "./conversions-tab";
import { ClicksTab } from "./clicks-tab";
import { FlowTab } from "./flow-tab";
import { LinksTab } from "./links-tab";
import { SettingsTab } from "./settings-tab";
import { HistoryTab } from "./history-tab";

export const CARD_TABS = ["summary", "report", "conversions", "clicks", "flow", "links", "settings", "history"] as const;
export type CardTab = (typeof CARD_TABS)[number];

const TAB_LABEL: Record<CardTab, TKey> = {
  summary: "card.tab.summary",
  report: "card.tab.report",
  conversions: "card.tab.conversions",
  clicks: "card.tab.clicks",
  flow: "card.tab.flow",
  links: "card.tab.links",
  settings: "card.tab.settings",
  history: "card.tab.history",
};

/** What a tab panel gets: the campaign, its list row, and its numbers. */
export interface CardContext {
  campaign: Campaign;
  item: CampaignListItem | undefined;
  /** Every metric over the Top period (the list's bag — one definition). */
  v: Bag | undefined;
  /** The same over the comparison window. */
  prev: Bag | undefined;
  loading: boolean;
  setTab: (tab: CardTab) => void;
  /** A tab's address with the period carried, for a link. */
  href: (tab: CardTab) => string;
}

/**
 * Карточка кампании (доски CSummary, CReport, CConversions, CClicks, CFlow,
 * CLinks, CSettings, CHistory, CActions, CArchive, CEmpty): one campaign under
 * the app's period, in eight tabs. The tab is the address
 * (`/campaigns/:id/<tab>`, decision 04), so «look at its conversions» is a
 * link and Back walks the tabs.
 *
 * The campaign changes in ONE place, the editor (`/campaigns/:id/edit`,
 * decision 05): «Изменить», «Изменить правила» and «Открыть редактор» go
 * there. Pause, copy and archive live in «⋯» — quiet, never the main action.
 *
 * On a phone (доска MCampaign) the same card and tabs: the shell's header
 * carries the name, the way back and «⋯»; «Сводка» draws its phone layout.
 */
export default function CampaignCardPage() {
  const { id } = useParams<{ id: string }>();
  const t = useT();
  const params = useReportParams();
  const phone = useIsPhone();
  const { tab, setTab, href } = useRouteTab(`/campaigns/${id ?? ""}`, CARD_TABS, "summary");

  const campaignQ = useGetCampaign(id);
  const campaign = campaignQ.data;
  const card = useCampaignCard(id, { from: params.from, to: params.to, tz: params.tz }, params.compareRange);
  const schema = useReportingSchema();
  const metrics = useMemo(() => schema.data?.metrics ?? [], [schema.data]);
  const item = card.data;
  const v = useMemo(() => (item && metrics.length > 0 ? derive(metrics, statsRowToNumbers(item.stats)) : undefined), [item, metrics]);
  const prev = useMemo(
    () => (item?.compareStats && metrics.length > 0 ? derive(metrics, statsRowToNumbers(item.compareStats)) : undefined),
    [item, metrics],
  );
  useCrumbTail(campaign?.name);

  if (campaignQ.isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-5 pt-4" aria-busy="true">
        <Skeleton className="h-8 w-96 max-w-full" />
        <Skeleton className="h-4 w-150 max-w-full" />
        <Skeleton className="h-8 w-180 max-w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="min-h-0 flex-1" />
      </div>
    );
  }
  if (campaignQ.error || !campaign) {
    return (
      <div className="flex h-full min-h-0 flex-col px-5 pb-5 pt-4">
        <PageState
          mode="error"
          title={t("campaign.notFound")}
          error={campaignQ.error ?? undefined}
          retry={campaignQ.error ? () => void campaignQ.refetch() : undefined}
          retryLabel={t("lib.common.retry")}
          back={
            <Link to="/campaigns" className="text-sm text-muted-foreground hover:text-foreground">
              ← {t("campaign.back")}
            </Link>
          }
        />
      </div>
    );
  }

  const ctx: CardContext = { campaign, item, v, prev, loading: card.isLoading || schema.isLoading, setTab, href };
  const leads = v?.leads;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <CampaignHeader
        phone={phone}
        campaign={campaign}
        item={item}
        onLinks={() => setTab("links")}
        onFlow={() => setTab("flow")}
        tabs={
          <TabBar
            aria-label={t("card.tabs")}
            value={tab}
            onValueChange={(next) => setTab(next as CardTab)}
            scrollable
            // The package's triggers shrink in its scrolling list, so a long
            // label («Ссылки и постбеки») broke onto two lines on a phone: a
            // tab keeps its width and the row scrolls instead.
            className="[&_[role=tab]]:shrink-0 [&_[role=tab]]:whitespace-nowrap"
            tabs={CARD_TABS.map((k) => ({
              value: k,
              label: t(TAB_LABEL[k]),
              count: k === "conversions" && leads !== undefined ? Math.round(leads) : undefined,
              panelId: "campaign-tab",
            }))}
          />
        }
      />
      <div id="campaign-tab" role="tabpanel" className="flex min-h-0 flex-1 flex-col px-5 pb-4 pt-3 max-md:px-3.5 max-md:pb-3">
        <TabPanel tab={tab} ctx={ctx} />
      </div>
    </div>
  );
}

function TabPanel({ tab, ctx }: { tab: CardTab; ctx: CardContext }) {
  switch (tab) {
    case "report":
      return <ReportTab ctx={ctx} />;
    case "conversions":
      return <ConversionsTab ctx={ctx} />;
    case "clicks":
      return <ClicksTab ctx={ctx} />;
    case "flow":
      return <FlowTab ctx={ctx} />;
    case "links":
      return <LinksTab ctx={ctx} />;
    case "settings":
      return <SettingsTab ctx={ctx} />;
    case "history":
      return <HistoryTab ctx={ctx} />;
    default:
      return <SummaryTab ctx={ctx} />;
  }
}
